// frontend/src/AdminGuard.js
// Password gate for admin routes. Prompts for the API secret,
// verifies it against the backend, and stores in sessionStorage
// for the duration of the browser tab.

import { useState, useEffect } from 'react';
import { API_URL, ADMIN_SESSION_KEY } from './config';

const SESSION_KEY = ADMIN_SESSION_KEY;

export default function AdminGuard({ children }) {
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // On mount, check if already verified this session
  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      // Re-verify the stored secret is still valid
      verify(stored, true);
    } else {
      setChecking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function verify(secret, silent = false) {
    if (!silent) setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/metrics`, {
        headers: { 'x-api-secret': secret },
      });
      if (res.ok) {
        sessionStorage.setItem(SESSION_KEY, secret);
        setVerified(true);
      } else {
        sessionStorage.removeItem(SESSION_KEY);
        if (!silent) setError('Invalid password.');
      }
    } catch {
      if (!silent) setError('Connection error. Please try again.');
    } finally {
      setChecking(false);
      setSubmitting(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!password.trim()) return;
    verify(password.trim());
  }

  // Still checking stored session
  if (checking) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={{ color: '#6b7a8d', fontSize: 14 }}>Verifying access…</p>
        </div>
      </div>
    );
  }

  // Verified — render admin content
  if (verified) return children;

  // Password prompt
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <svg width="28" height="28" viewBox="0 0 64 64" style={{ flexShrink: 0 }}>
            <rect width="64" height="64" rx="16" fill="#1f4e79" />
            <path d="M32 47 C32 47 20 34.5 20 26 A12 12 0 1 1 44 26 C44 34.5 32 47 32 47 Z" fill="none" stroke="#fff" strokeWidth="4.2" strokeLinejoin="round" />
            <circle cx="32" cy="26" r="4.8" fill="#EF9F27" />
          </svg>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1a3a5c' }}>Admin Access</div>
            <div style={{ fontSize: 12, color: '#8a95a3' }}>Your Scholarship Finder</div>
          </div>
        </div>

        {error && (
          <div style={styles.error}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <label style={styles.label}>Admin password</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter admin password"
            autoFocus
            style={styles.input}
          />
          <button
            type="submit"
            disabled={submitting || !password.trim()}
            style={{
              ...styles.button,
              opacity: submitting || !password.trim() ? 0.6 : 1,
              cursor: submitting || !password.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Verifying…' : 'Continue'}
          </button>
        </form>

        <a href="/" style={styles.backLink}>← Back to site</a>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f0f4f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'sans-serif',
    padding: 20,
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '32px 36px',
    width: '100%',
    maxWidth: 380,
    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
    border: '1px solid #e2e8f0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
    paddingBottom: 20,
    borderBottom: '1px solid #e2e8f0',
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 14px',
    border: '1.5px solid #d0d8e4',
    borderRadius: 8,
    fontSize: 14,
    color: '#2c3e50',
    background: '#fafcff',
    marginBottom: 16,
    outline: 'none',
  },
  button: {
    width: '100%',
    padding: '11px 20px',
    background: '#1f4e79',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'sans-serif',
  },
  error: {
    background: '#fdf0ef',
    border: '1px solid #f5c6cb',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 13,
    color: '#c0392b',
    marginBottom: 16,
  },
  backLink: {
    display: 'block',
    textAlign: 'center',
    marginTop: 16,
    fontSize: 13,
    color: '#8a95a3',
    textDecoration: 'none',
  },
};
